import { IconRefresh } from '@tabler/icons-react'

import { Button } from '@/components/sui/button'
import { Card, CardContent } from '@/components/sui/card'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { cn } from '@/lib/utils'

import locales from './locales'

interface DataLoadErrorProps {
  /** Context-specific message, e.g. "Failed to load white-label settings." */
  message: string
  onRetry: () => void
  /** `block`: centered card (tables/pages). `inline`: destructive box with a text link. */
  variant?: 'block' | 'inline'
  className?: string
  /** Overrides the default localized retry label. */
  retryLabel?: string
}

export function DataLoadError({
  message,
  onRetry,
  variant = 'block',
  className,
  retryLabel,
}: DataLoadErrorProps) {
  const { t } = useComponentTranslation(locales)
  const label = retryLabel ?? t('retry')

  if (variant === 'inline') {
    return (
      <div
        className={cn(
          'border-destructive/50 bg-destructive/10 rounded-lg border p-4',
          className,
        )}
      >
        <p className="text-destructive text-sm">
          {message}{' '}
          <button
            type="button"
            onClick={onRetry}
            className="cursor-pointer underline hover:no-underline"
          >
            {label}
          </button>
        </p>
      </div>
    )
  }

  return (
    <Card className={className}>
      <CardContent className="flex flex-col items-center justify-center py-12">
        <p className="text-muted-foreground mb-4">{message}</p>
        <Button variant="outline" onClick={onRetry}>
          <IconRefresh className="h-4 w-4" />
          {label}
        </Button>
      </CardContent>
    </Card>
  )
}
